import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import SidebarMenu from "../../Administrador/AdminPanel/SidebarMenu";
import TopBar from "../../Administrador/AdminPanel/TopBar";
import InfoInvoice from "./InfoInvoice";
import InvoiceDetail from "./InvoiceDetail";
import InvoiceCheck from "./InvoiceCheck/InvoiceCheck";

const normalizeProducts = (productos) => {
  if (!Array.isArray(productos)) return [];

  return productos.map((r) => ({
    product_id: r.product_id ?? r.product_id_ref ?? null,
    variant_id: r.variant_id ?? r.variant_id_ref ?? null,
    code: r.code || "",
    sku: r.sku || "",
    variant_type: r.variant_type || "",
    value: r.value || r.variant_value || "",
    codigo_principal: r.codigo_principal || "",
    codigo_auxiliar: r.codigo_auxiliar || "",
    descripcion: r.descripcion || "",
    detalles: r.detalles || r.detalle || r.detalle_adicional || "",
    cantidad: String(r.cantidad ?? "1"),
    pvp: String(r.pvp ?? r.precio_unitario ?? "0"),
    iva_porcentaje: Number(r.iva_porcentaje ?? 0),
    descuentoPct: String(r.descuentoPct ?? r.descuento_pct ?? "0"),
    bodega_id: r.bodega_id ?? r.bodega ?? "",
    bodega: r.bodega ?? r.bodega_id ?? "",
  }));
};

const NewInvoice = () => {
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [invoiceInfo, setInvoiceInfo] = useState({});
  const [productRows, setProductRows] = useState([]);
  const [productDescripcion, setProductDescripcion] = useState("");
  const [invoiceData, setInvoiceData] = useState({});
  const [payments, setPayments] = useState([]);

  const [documentoId, setDocumentoId] = useState(null);
  const [invoiceStatus, setInvoiceStatus] = useState("");
  const [isDraft, setIsDraft] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const prefillOnceRef = useRef(false);

  useEffect(() => {
    const isSuper = localStorage.getItem("is_superuser") === "true";
    const isAdmin = localStorage.getItem("is_admin") === "true";
    const isVendedor = localStorage.getItem("is_vendedor") === "true";

    if (!isSuper && !isAdmin && !isVendedor) {
      navigate("/login");
    }
  }, [location, navigate]);

  useEffect(() => {
    if (prefillOnceRef.current) return;

    const prefill = location?.state?.prefill_invoice;
    if (!prefill) return;

    prefillOnceRef.current = true;

    const info = prefill.info || {};
    const productosContainer = prefill.productos || {};

    const rawRows = Array.isArray(productosContainer?.productos)
      ? productosContainer.productos
      : Array.isArray(prefill.productRows)
      ? prefill.productRows
      : Array.isArray(prefill.productos_rows)
      ? prefill.productos_rows
      : Array.isArray(prefill.productos_raw)
      ? prefill.productos_raw
      : Array.isArray(prefill.rows)
      ? prefill.rows
      : Array.isArray(prefill.productos)
      ? prefill.productos
      : [];

    const mappedRows = normalizeProducts(rawRows);

    const desc =
      info.descripcion_factura ||
      info.descripcion ||
      prefill.descripcion_factura ||
      prefill.descripcion ||
      "";

    const pay = Array.isArray(prefill.pagos) ? prefill.pagos : [];

    setInvoiceInfo(info || {});
    setProductRows(mappedRows);
    setProductDescripcion(String(desc || ""));
    setPayments(pay);

    if (info?.bodega_id || info?.bodega) {
      setInvoiceData((prev) => ({
        ...(prev || {}),
        bodega_id: info.bodega_id || info.bodega || "",
        bodega: info.bodega || info.bodega_id || "",
      }));
    }

    if (prefill.documento_id) {
      setDocumentoId(prefill.documento_id);
    }

    if (prefill.estado) {
      setInvoiceStatus(prefill.estado);
    }

    if (typeof prefill.es_borrador === "boolean") {
      setIsDraft(prefill.es_borrador);
    } else if (prefill.modo === "edit_draft") {
      setIsDraft(true);
      setInvoiceStatus("BORRADOR");
    }
  }, [location]);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("access");

      await fetch("/sistema/logout/", {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch {}

    localStorage.clear();
    navigate("/login");
  };

  const handleInvoiceInfoChange = (info) => {
    setInvoiceInfo(info);

    if (info?.tipo_comprobante === "Retención") {
      setPayments([]);
      setProductRows([]);
      setProductDescripcion("");
    }
  };

  const handleProductsLoaded = (productos) => {
    setProductRows(normalizeProducts(productos));
  };

  const invoiceInfoWithBodega = useMemo(() => {
    const bodegaId = invoiceData?.bodega_id || invoiceData?.bodega || invoiceInfo?.bodega_id || invoiceInfo?.bodega || "";

    return {
      ...(invoiceInfo || {}),
      bodega_id: bodegaId,
      bodega: bodegaId,
    };
  }, [invoiceInfo, invoiceData]);

  const hideDetailForRetention = invoiceInfo?.tipo_comprobante === "Retención";

  return (
    <Box sx={{ display: "flex", background: "#f5f6fa", minHeight: "100vh" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        handleLogout={handleLogout}
        navigate={navigate}
      />

      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ p: 3 }}>
          <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#1565C0", mb: 2 }}>
              Registrar Documento Electrónico
            </Typography>

            <InfoInvoice
              onChange={handleInvoiceInfoChange}
              initialData={invoiceInfo}
              onProductsLoaded={handleProductsLoaded}
            />

            {!hideDetailForRetention && (
              <InvoiceDetail
                invoiceInfo={invoiceInfoWithBodega}
                invoiceData={invoiceData}
                onInvoiceDataChange={setInvoiceData}
                payments={payments}
                setPayments={setPayments}
                productRows={productRows}
                setProductRows={setProductRows}
                productDescripcion={productDescripcion}
                setProductDescripcion={setProductDescripcion}
              />
            )}

            <InvoiceCheck
              invoiceInfo={invoiceInfoWithBodega}
              productDescripcion={productDescripcion}
              invoiceData={invoiceData}
              payments={payments}
              documentoId={documentoId}
              setDocumentoId={setDocumentoId}
              invoiceStatus={invoiceStatus}
              setInvoiceStatus={setInvoiceStatus}
              isDraft={isDraft}
              setIsDraft={setIsDraft}
            />
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default NewInvoice;